/**
 * Gamification Types
 * 
 * Defines types for the global streak system, points economy, and achievements.
 * All level completions from ANY gameplay mode count toward streaks and points.
 */

export type GameplayMode = 'courses' | 'just_play' | 'daily_challenge' | 'tournaments' | 'weekly_contests';

export interface LevelActivity {
  levelId: string;
  mode: GameplayMode;
  timestamp: Date;
  metadata?: {
    language?: string;
    keyboardId?: string;
    wpm?: number;
    tpm?: number;
    accuracy?: number;
    [key: string]: any;
  };
}

export interface DailyActivity {
  date: string; // YYYY-MM-DD format
  activities: LevelActivity[];
  totalLevels: number;
  modeBreakdown: {
    mode: GameplayMode;
    count: number;
  }[];
}

export interface GlobalStreak {
  consecutiveDays: number;
  todayGoalMet: boolean;
  todayLevelsCompleted: number;
  requiredLevels: number;
  longestStreak?: number;
}

export interface StreakBadge {
  name: string;
  icon: string;
  minDays: number;
  maxDays?: number;
  color: string;
  description: string;
}

export const STREAK_BADGES: StreakBadge[] = [
  // No badge for 1-6 days (just starting out)
  {
    name: 'Starter',
    icon: '🌱',
    minDays: 7,
    maxDays: 13,
    color: '#38bdf8',
    description: 'One week strong!'
  },
  {
    name: 'Committed',
    icon: '🌿',
    minDays: 14,
    maxDays: 29,
    color: '#a78bfa',
    description: 'Two weeks of dedication'
  },
  {
    name: 'Champion',
    icon: '🏆',
    minDays: 30,
    maxDays: 59,
    color: '#fbbf24',
    description: 'A full month achieved!'
  },
  {
    name: 'Master',
    icon: '🎖️',
    minDays: 60,
    maxDays: 99,
    color: '#f97316',
    description: 'Two months of mastery!'
  },
  {
    name: 'Legend',
    icon: '🦅',
    minDays: 100,
    maxDays: 179,
    color: '#00e5ff',
    description: 'Legendary status!'
  },
  {
    name: 'Hero',
    icon: '🛡️',
    minDays: 180,
    maxDays: 364,
    color: '#d946ef',
    description: 'Half a year conquered!'
  },
  {
    name: 'Royal',
    icon: '👑',
    minDays: 365,
    maxDays: 729,
    color: '#ffd700',
    description: 'One year of excellence!'
  },
  {
    name: 'Titan',
    icon: '🔱',
    minDays: 730,
    maxDays: 1824,
    color: '#c084fc',
    description: 'Two years unbroken!'
  },
  {
    name: 'Zeus',
    icon: '⚡',
    minDays: 1825,
    maxDays: 3649,
    color: '#facc15',
    description: 'Five years of power!'
  },
  {
    name: 'Eternal',
    icon: '🌌',
    minDays: 3650,
    color: '#ffffff',
    description: 'A decade of dedication!'
  }
];

/**
 * Point bonuses awarded one-time when a user first reaches each streak badge.
 * Keys match STREAK_BADGES[].name.
 */
export const STREAK_BADGE_BONUSES: Record<string, number> = {
  Starter: 25,
  Committed: 50,
  Champion: 100,
  Master: 200,
  Legend: 350,
  Hero: 600,
  Royal: 1000,
  Titan: 2000,
  Zeus: 5000,
  Eternal: 10000,
};

export interface ExtraPracticeTier {
  tier: number;
  levelsRequired: number;
  pointsBonus: number;
  crownSize: 'small' | 'medium' | 'large';
  label: string;
}

/**
 * Extra Practice Tiers - aligned with ring milestones
 * These should match ring2, ring3, ring4 from gameplaySettings
 * Default values: 8, 16, 32 (configurable by admin)
 */
export const EXTRA_PRACTICE_TIERS: ExtraPracticeTier[] = [
  {
    tier: 1,
    levelsRequired: 8,  // Matches ring2
    pointsBonus: 10,
    crownSize: 'small',
    label: 'Extra Practice'
  },
  {
    tier: 2,
    levelsRequired: 16, // Matches ring3
    pointsBonus: 25,
    crownSize: 'medium',
    label: 'Super Practice'
  },
  {
    tier: 3,
    levelsRequired: 32, // Matches ring4
    pointsBonus: 50,
    crownSize: 'large',
    label: 'Ultimate Practice'
  }
];

export type PointTransactionType = 'level' | 'streak' | 'extra' | 'time_travel' | 'purchase' | 'bonus';

export interface PointTransaction {
  id: string;
  type: PointTransactionType;
  amount: number;
  date: string;
  metadata?: {
    levelId?: string;
    mode?: GameplayMode;
    tier?: number;
    purchaseItem?: string;
    [key: string]: any;
  };
}

export interface UserPoints {
  total: number;
  earned: number;
  spent: number;
  history: PointTransaction[];
}

export const POINTS_CONFIG = {
  LEVEL_COMPLETION: 1,
  STREAK_BONUS: 5,
  EXTRA_TIER_1: 10,
  EXTRA_TIER_2: 25,
  EXTRA_TIER_3: 50,
  
  // Spending costs — re-anchored on the normal (minimum-streak) player ≈ 8 pts/day.
  // Streak protection is the cheapest tier: the normal player needs it most and
  // earns least. See STORE_SYSTEM.md §1–§3.
  STREAK_FREEZE_NORMAL: 40,   // ~5 days of normal-player income
  STREAK_FREEZE_WEEKEND: 80,  // 2× normal, covers 2 days
  // Time Machine cost = BASE + days_back × PER_DAY. The base fee keeps a 1-day
  // repair (45) just above a Normal Freeze (40), so reactive repair never
  // undercuts proactive insurance. Anti-farm no longer lives in the price — the
  // 30-day lookback + 14-day cooldown block rebuilding a dead streak, so the
  // price is free to be affordable to the normal player (30-day repair = 190).
  TIME_TRAVEL_BASE: 40,
  TIME_TRAVEL_PER_DAY: 5,
  TSHIRT: 5000
};

/**
 * Aggregation types for efficient filtering and breakdown views
 */

export interface ActivityAggregation {
  type: 'keyboard' | 'language' | 'mode' | 'course' | 'all';
  filterValue: string; // keyboard ID, language, mode, or 'all'
  dateRange: { start: string; end: string };
  dailyBreakdown: {
    date: string;
    levelCount: number;
    sessions: number;
    avgWpm?: number;
    avgTpm?: number;
    avgAccuracy?: number;
  }[];
  totalLevels: number;
  totalSessions: number;
  lastUpdated: Date;
}

